/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DownOutlined } from '@ant-design/icons';
import './ModalCustomSelect.css';

/**
 * Single-select for use inside high-z-index Ant Design modals.
 * Renders the menu in a portal with position:fixed so it is never trapped
 * behind modal/footer stacking contexts (unlike antd Select in some setups).
 */
const ModalCustomSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  showSearch = false,
  disabled = false,
  /** Must be greater than the parent Modal z-index */
  dropdownZIndex = 5000200,
  className = '',
  onSelect,
  id,
  /** When false (e.g. parent Modal closed), force-close the dropdown */
  parentOpen = true,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, maxHeight: 280 });

  const updatePosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom - 8;
    const maxHeight = Math.min(280, Math.max(120, spaceBelow));
    setCoords({
      top: r.bottom + 4,
      left: r.left,
      width: Math.max(r.width, 160),
      maxHeight,
    });
  }, []);

  useEffect(() => {
    if (!parentOpen) {
      setOpen(false);
      setSearch('');
    }
  }, [parentOpen]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition, value, options.length]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
      setSearch('');
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const q = (search || '').trim().toLowerCase();
  const filtered = options.filter((opt) => {
    const label = String(opt.label ?? opt.value ?? '');
    return !q || label.toLowerCase().includes(q);
  });

  const displayLabel = value != null && value !== '' ? String(value) : null;

  const handlePick = (opt) => {
    if (opt.disabled) return;
    const v = opt.value;
    if (onChange) onChange(v);
    if (onSelect) onSelect(v);
    setOpen(false);
    setSearch('');
  };

  const toggle = () => {
    if (disabled) return;
    setOpen((o) => !o);
    if (!open) setSearch('');
  };

  const dropdown =
    open &&
    createPortal(
      <div
        ref={dropdownRef}
        className="modal-custom-select-dropdown"
        style={{
          position: 'fixed',
          top: coords.top,
          left: coords.left,
          width: coords.width,
          maxHeight: coords.maxHeight,
          zIndex: dropdownZIndex,
        }}
        role="listbox"
      >
        {showSearch && (
          <div className="modal-custom-select-search-wrap">
            <input
              type="text"
              className="modal-custom-select-search"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              autoFocus
              autoComplete="off"
            />
          </div>
        )}
        <ul className="modal-custom-select-list">
          {filtered.length === 0 ? (
            <li className="modal-custom-select-empty">No matches</li>
          ) : (
            filtered.map((opt, idx) => {
              const val = opt.value;
              const label = opt.label != null ? opt.label : val;
              const selected = val === value;
              return (
                <li key={opt.key != null ? opt.key : `${val}-${idx}`}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    disabled={opt.disabled}
                    className={`modal-custom-select-option${selected ? ' is-selected' : ''}${
                      opt.disabled ? ' is-disabled' : ''
                    }`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handlePick(opt)}
                  >
                    {label}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>,
      document.body
    );

  return (
    <div
      ref={wrapRef}
      className={`modal-custom-select-wrap ${className}`.trim()}
      id={id}
    >
      <button
        type="button"
        className={`modal-custom-select-trigger${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={displayLabel ? 'modal-custom-select-value' : 'modal-custom-select-placeholder'}>
          {displayLabel || placeholder}
        </span>
        <DownOutlined className="modal-custom-select-chevron" />
      </button>
      {dropdown}
    </div>
  );
};

export default ModalCustomSelect;
